// ============================================================================
// MÓDULO: CLIENT AREA (LOGIN & MFA GATEWAY)
// ARQUIVO: src/features/auth/components/ClientArea.jsx
// OBJETIVO: Restaurado automaticamente via Auto-Patcher.
// ============================================================================

import React, { useState } from 'react';
import {
    getMultiFactorResolver,
    TotpMultiFactorGenerator,
    sendPasswordResetEmail,
    EmailAuthProvider,
    reauthenticateWithCredential
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../../shared/api/firebase/config';
import { loginUser } from '../../../services/AuthRepository';
import { ASSETS } from '../../../config/Config';

export const ClientArea = ({ user }) => {
  const [authMode, setAuthMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // MFA States
  const [mfaResolver, setMfaResolver] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showMfaInput, setShowMfaInput] = useState(false);
  const [mfaError, setMfaError] = useState('');

  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // --- 1. LOGIN PADRÃO (COM SUPORTE A MFA) ---
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await loginUser(email, password);
    
    if (result.success) {
        setLoading(false);
        // Sucesso: O App.jsx fará o redirecionamento via useEffect
    } else {
        // --- TRATAMENTO DE MFA (MULTI-FACTOR) ---
        if (result.error && result.error.code === 'auth/multi-factor-auth-required') {
             const resolver = getMultiFactorResolver(auth, result.error);
             // Verifica se o fator exigido é TOTP (Google Authenticator)
             if (resolver.hints[0].factorId === TotpMultiFactorGenerator.FACTOR_ID) {
                 setMfaResolver(resolver);
                 setShowMfaInput(true);
                 setLoading(false);
                 return;
             }
        }

        // TRADUÇÃO DE ERROS
        let msg = result.error.message || result.error;
        if (msg.includes("invalid-credential") || msg.includes("user-not-found")) {
            msg = "Acesso negado. E-mail não cadastrado ou senha incorreta.";
        } else if (msg.includes("too-many-requests")) {
            msg = "Muitas tentativas falhas. Aguarde alguns instantes.";
        }
        
        setError(msg);
        setLoading(false);
    }
  };

  // --- 2. VALIDAÇÃO MFA ---
  const handleMfaSubmit = async () => {
      if (!verificationCode) return;
      setLoading(true);
      try {
          const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
              mfaResolver.hints[0].uid,
              verificationCode
          );
          await mfaResolver.resolveSignIn(multiFactorAssertion);
          setShowMfaInput(false);
      } catch (e) {
          setMfaError("Código inválido. Tente novamente.");
          setLoading(false);
      }
  };

  // --- 3. RECUPERAÇÃO DE SENHA ---
  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccessMsg('');
    try {
        await sendPasswordResetEmail(auth, email);
        setSuccessMsg("Link de redefinição enviado!\nVerifique seu e-mail.");
        setLoading(false);
    } catch (e) {
        let msg = e.message;
        if(msg.includes("user-not-found")) msg = "Este e-mail não consta em nossa base de clientes.";
        setError(msg);
        setLoading(false);
    }
  };

  // --- 4. RESET DE QR CODE (BACKEND) ---
  const handleMfaResetSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      try {
          const requestReset = httpsCallable(functions, 'requestMfaReset');
          const response = await requestReset({ email: email });
          if (response.data.success) {
              setSuccessMsg("✅ Instruções enviadas!\nVerifique seu e-mail para resetar o QR Code.");
              setLoading(false);
          } else {
              setError("Erro: " + response.data.message);
              setLoading(false);
          }
      } catch (e) {
          setError("Erro de Conexão: " + e.message);
          setLoading(false);
      }
  };

  if (user) {
      return (
          <div className="flex h-screen items-center justify-center flex-col">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mb-4"></div>
              <p className="text-gray-500 font-bold uppercase tracking-wider text-xs">Autenticando...</p>
          </div>
      );
  }

  return (
    <div className="py-20 bg-white min-h-[70vh] flex items-center relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-md mx-auto bg-white p-8 rounded-2xl border border-gray-200 shadow-2xl">
            
            <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-[#20243B] uppercase tracking-wide">
                    {showMfaInput ? 'Segurança Adicional' : (
                        authMode === 'login' ? 'Portal do Cliente' :
                        authMode === 'forgot' ? 'Redefinir Senha' : 'Resetar QR Code (MFA)'
                    )}
                </h3>
                {authMode !== 'login' && !showMfaInput && (
                    <button type="button" onClick={() => { setAuthMode('login'); setError(''); setSuccessMsg(''); }} className="text-xs text-gray-400 hover:text-brand-blue mt-2 underline font-bold flex items-center justify-center gap-1 w-full">
                        <i className="fas fa-arrow-left"></i> Voltar ao Login
                    </button>
                )}
            </div>

            {error && <div className="mb-4 bg-red-50 text-red-700 text-xs font-bold p-4 rounded border-l-4 border-red-500 text-center whitespace-pre-wrap shadow-sm">{error}</div>}
            {successMsg && <div className="mb-4 bg-green-50 text-green-700 text-xs font-bold p-4 rounded border-l-4 border-green-500 text-center whitespace-pre-wrap shadow-sm">{successMsg}</div>}

            {authMode === 'login' && !showMfaInput && (
                <div className="space-y-6 animate-fade-in-up">
                    <form data-testid="login-form" onSubmit={handleLoginSubmit} className="space-y-4">
                        <div className="relative">
                            <i className="fas fa-envelope absolute left-3 top-3.5 text-gray-400"></i>
                            <input data-testid="login-email" type="email" placeholder="E-mail Cadastrado" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 p-3 border rounded-lg text-sm bg-gray-50 focus:border-brand-blue outline-none transition-all placeholder-gray-400 text-gray-800 font-medium" required />
                        </div>
                        <div className="relative">
                            <i className="fas fa-lock absolute left-3 top-3.5 text-gray-400"></i>
                            <input data-testid="login-password" type="password" placeholder="Sua Senha" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 p-3 border rounded-lg text-sm bg-gray-50 focus:border-brand-blue outline-none transition-all placeholder-gray-400 text-gray-800" required />
                        </div>
                        
                        <button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#20243B] text-white py-4 rounded-lg font-bold shadow-lg hover:bg-[#1a1e30] transition-transform active:scale-95 disabled:opacity-50 text-sm tracking-wider">
                            {loading ? 'ACESSANDO...' : 'ENTRAR NO SISTEMA'}
                        </button>
                    </form>
                    
                    <div className="mt-6 bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
                        <p className="text-brand-blue font-bold text-xs uppercase mb-1">
                            <i className="fas fa-shield-alt mr-1"></i> Acesso Restrito
                        </p>
                        <p className="text-gray-600 text-xs leading-relaxed">
                            Esta área é exclusiva para clientes do escritório.
                        </p>
                    </div>

                    <div className="flex justify-between pt-4 border-t border-gray-100 mt-4">
                        <button type="button" onClick={() => setAuthMode('forgot')} className="text-xs font-bold text-gray-500 hover:text-blue-600 transition-colors">
                            Esqueci a Senha
                        </button>
                        <button type="button" onClick={() => setAuthMode('mfa-reset')} className="text-xs font-bold text-gray-500 hover:text-red-600 transition-colors">
                            Perdi o Celular (MFA)
                        </button>
                    </div>
                </div>
            )}
            
            {showMfaInput && (
                <div className="space-y-4 animate-fade-in-up">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-center">
                        <i className="fas fa-shield-alt text-3xl text-blue-600 mb-2"></i>
                        <p className="text-sm font-bold text-blue-900">Autenticação em 2 Fatores</p>
                        <p className="text-xs text-blue-700">Digite o código do seu aplicativo (Google Authenticator).</p>
                    </div>
                    <input type="text" placeholder="000000" maxLength="6" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g,''))} className="w-full p-4 border rounded-lg text-center text-2xl tracking-[0.5em] font-mono focus:border-brand-blue outline-none" autoFocus />
                    {mfaError && <p className="text-red-500 text-xs font-bold text-center">{mfaError}</p>}
                    <button onClick={handleMfaSubmit} disabled={loading || verificationCode.length !== 6} className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 transition-colors">
                        {loading ? 'VERIFICANDO...' : 'CONFIRMAR ACESSO'}
                    </button>
                    <button onClick={() => { setShowMfaInput(false); setAuthMode('mfa-reset'); }} className="w-full text-xs text-red-400 hover:text-red-600 font-bold mt-4 underline text-center block">Não tenho o código</button>
                </div>
            )}
            
            {authMode === 'forgot' && (
                <form onSubmit={handleForgotSubmit} className="space-y-4 animate-fade-in-up">
                    <p className="text-xs text-center text-gray-500">Digite seu e-mail para receber o link de redefinição.</p>
                    <input type="email" placeholder="E-mail Cadastrado" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 border rounded-lg text-sm bg-gray-50 focus:border-brand-blue outline-none" required />
                    <button type="submit" disabled={loading} className="w-full bg-blue-600 text-white py-4 rounded-lg font-bold shadow-lg hover:bg-blue-700 transition-colors">
                        {loading ? 'ENVIANDO...' : 'ENVIAR LINK'}
                    </button>
                </form>
            )}

            {authMode === 'mfa-reset' && (
                <form onSubmit={handleMfaResetSubmit} className="space-y-4 animate-fade-in-up">
                    <div className="bg-red-50 p-4 rounded-lg border border-red-100 text-center mb-2">
                        <i className="fas fa-unlock-alt text-3xl text-red-500 mb-2"></i>
                        <p className="text-sm font-bold text-red-800 mb-1">Reset de Segurança</p>
                        <p className="text-xs text-red-700 leading-relaxed">
                            Insira seu e-mail. Se confirmado, enviaremos um link seguro para remover o QR Code perdido.
                        </p>
                    </div>
                    <input type="email" placeholder="Seu E-mail" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 border rounded-lg text-sm focus:border-red-500 outline-none" required />
                    <button type="submit" disabled={loading} className="w-full bg-red-600 text-white py-4 rounded-lg font-bold shadow-lg hover:bg-red-700 transition-colors">
                        {loading ? 'PROCESSANDO...' : 'ENVIAR SOLICITAÇÃO'}
                    </button>
                </form>
            )}
          </div>
      </div>
    </div>
  );
};